import { useEffect, useState } from "react";
import { CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ListenButton from "../../components/ListenButton";
import GameShell from "./GameShell";

export default function ComprehensionGame() {
    const [data, setData] = useState([]);
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [correct, setCorrect] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/games/comprehension").then((r) => setData(r.data));
    }, []);

    const pick = (qi, oi) => {
        if (submitted) return;
        setAnswers((a) => ({ ...a, [qi]: oi }));
    };

    const submit = async () => {
        const passage = data[idx];
        let c = 0;
        passage.questions.forEach((q, qi) => {
            if (answers[qi] === q.answer) c++;
        });
        const total = passage.questions.length;
        const newCorrect = correct + c;
        setCorrect(newCorrect);
        setSubmitted(true);
        if (c === total) toast.success("Excellent reading!");
        else toast.message(`You got ${c} of ${total}.`);

        if (idx + 1 >= data.length) {
            setDone(true);
            try {
                await api.post("/progress/game-score", { game: "comprehension", score: newCorrect * 15, correct: newCorrect, total: data.reduce((s, p) => s + p.questions.length, 0) });
                toast.success(`+${newCorrect * 15} XP earned`);
            } catch {}
        }
    };

    const nextPassage = () => {
        setIdx(idx + 1);
        setAnswers({});
        setSubmitted(false);
    };

    const reset = () => {
        setIdx(0); setAnswers({}); setSubmitted(false); setCorrect(0); setDone(false);
    };

    if (!data.length) return <GameShell title="Read & Understand"><p>Loading…</p></GameShell>;

    const passage = data[idx];

    return (
        <GameShell
            title="Read & Understand"
            intro="Read the story, then answer about it."
            testId="comprehension-game"
            instructions="This is the Read and Understand game. Read the short story at the top. Then answer the question below by tapping the best choice. You can use the Listen button to hear the story read aloud."
        >
            <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-[#819888]">Passage {idx + 1} of {data.length}</p>
                <p className="text-sm text-[#2D5D4E] font-medium" data-testid="co-score">Correct: {correct}</p>
            </div>

            {done ? (
                <div className="card-rw p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#3A7D5C] mx-auto mb-4" strokeWidth={1.6} />
                    <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit" }}>Nicely done</h2>
                    <p className="text-lg text-[#515B57] mb-6">{correct} questions correct in total.</p>
                    <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] font-medium" data-testid="co-replay">
                        <RotateCcw className="w-4 h-4" /> Play again
                    </button>
                </div>
            ) : (
                <>
                    <article className="card-rw p-8 mb-6" data-testid="co-passage">
                        <p className="text-lg leading-[1.75] text-[#1C2321]">{passage.passage}</p>
                        <div className="mt-5">
                            <ListenButton text={passage.passage} label="Read aloud to me" size="sm" testId="co-listen" />
                        </div>
                    </article>

                    <div className="space-y-6 mb-6">
                        {passage.questions.map((q, qi) => (
                            <div key={qi} className="card-rw p-6" data-testid={`co-q-${qi}`}>
                                <p className="text-lg font-medium mb-4">{qi + 1}. {q.q}</p>
                                <div className="space-y-2">
                                    {q.options.map((o, oi) => {
                                        const selected = answers[qi] === oi;
                                        const showCorrect = submitted && oi === q.answer;
                                        const showWrong = submitted && selected && oi !== q.answer;
                                        const cls = showCorrect
                                            ? "border-[#3A7D5C] bg-[#E8F1EA] text-[#1F4237]"
                                            : showWrong
                                            ? "border-[#B24C4C] bg-[#F8E6E6] text-[#7d2f2f]"
                                            : selected
                                            ? "border-[#2D5D4E] bg-[#E0EDE6]"
                                            : "border-[#E5E7E2] bg-white hover:bg-[#FAF9F6]";
                                        return (
                                            <button
                                                key={oi}
                                                onClick={() => pick(qi, oi)}
                                                className={`w-full text-left px-5 py-3 rounded-lg border ${cls} transition-colors`}
                                                data-testid={`co-q-${qi}-opt-${oi}`}
                                                disabled={submitted}
                                            >
                                                {o}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    {!submitted ? (
                        <button
                            onClick={submit}
                            disabled={Object.keys(answers).length !== passage.questions.length}
                            className="w-full py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium hover:bg-[#1F4237] disabled:opacity-50"
                            data-testid="co-submit"
                        >
                            Submit answers
                        </button>
                    ) : (
                        idx + 1 < data.length && (
                            <button onClick={nextPassage} className="w-full py-3.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-lg font-medium" data-testid="co-next">
                                Next passage
                            </button>
                        )
                    )}
                </>
            )}
        </GameShell>
    );
}
